import { useMemo } from 'react'

// Constants
import { paths } from '../constants/routes'

// Types
import { INavTabs } from '../components/SecondaryNavigation'
import { IUser } from '../common/models/User'
import { TeamRole } from '../common/enums/userCompanyRole.enum'
import { ICompany } from '../common/models/Company'
import { useModuleAccess } from '../hooks/useModuleAccess'

export interface IHistoryState {
  termNumber?: string
  teamId?: string
  from:
    | 'people'
    | 'progress'
    | 'profiles'
    | 'culture'
    | 'ownDashboard'
    | 'team'
    | 'review'
    | 'companySettings'
    | 'teamSettings-:teamId'
    | 'onboardingDashboard'
    | 'peopleProfile:userId'
    | 'rituals'
    | 'peopleLibrary'
    | 'teamLibrary'
}

// Reset the list to the top on tab switch, but keep the tabs pinned if the header
// was already scrolled away — don't reveal more of the header than was showing.
export const scrollToPinnedTabs = () => {
  const nav = document.getElementById('secondaryNav')
  const tabs = document.getElementById('secondaryNavTabs')
  if (!nav || !tabs) return window.scrollTo({ top: 0 })
  const headerHeight = nav.offsetHeight - tabs.offsetHeight
  const pinPoint = Math.max(
    0,
    nav.getBoundingClientRect().top + window.scrollY + headerHeight - 56
  )
  window.scrollTo({ top: Math.min(window.scrollY, pinPoint) })
}

export const useCompanyTabs = (user?: IUser, company?: ICompany) => {
  const {
    performanceManagement,
    employerBranding,
    onboarding,
  } = useModuleAccess()

  // Built deterministically per (user, company) so the tab set is stable and a
  // disabled module never leaves an empty shell. Order: Dashboard, Culture,
  // People, Profiles, Library, Rituals (matches the previous behaviour).
  const companyTabs = useMemo<INavTabs[]>(() => {
    const isAdmin = !!user?.asCompanyMember?.isAdmin

    const hasCultureValues =
      company?.cultureValues?.some(
        (value) => value.name !== undefined && value.name.length !== 0
      ) ?? false
    const hasFiles = (company?.files?.length ?? 0) > 0

    const tabs: INavTabs[] = []
    // Culture is foundational company content, available regardless of modules.
    // (Only culture alignment inside the page is gated by performance management.)
    if (hasCultureValues) {
      tabs.push({
        name: 'Culture',
        title: 'general.feedback.culture',
        path: '/company/culture',
      })
    }
    // People roster is primarily an entry point into performance management.
    if (performanceManagement) {
      tabs.push({
        name: 'People',
        title: 'general.people',
        path: '/company/people',
      })
    }
    // Profiles (org structure) is structural and always available.
    tabs.push({
      name: 'Profiles',
      title: 'general.profiles',
      path: '/company/profiles',
    })
    // Admins always see the library (so they can upload the first file);
    // regular members only see it once files exist.
    if (isAdmin || hasFiles) {
      tabs.push({
        name: 'Library',
        title: 'general.library',
        path: '/company/library',
      })
    }
    // Rituals hub stays if any module is enabled (sections gate themselves).
    if (isAdmin && (performanceManagement || onboarding || employerBranding)) {
      tabs.push({
        name: 'Rituals',
        title: 'general.rituals',
        path: '/company/rituals',
      })
    }

    return tabs
  }, [user, company, performanceManagement, employerBranding, onboarding])

  return {
    companyTabs,
  }
}

export const getProfileBreadcrumbs = (
  company?: ICompany,
  historyState?: IHistoryState,
  userRole?: TeamRole
) => {
  const from = historyState?.from
  if (!from) return
  if (from === 'review') {
    return userRole && userRole === TeamRole.LEAD
      ? [
          {
            name: 'navHelper.evaluationsProgress',
            route: `/company/rituals/evaluations`,
          },
        ]
      : [
          {
            name: 'general.company',
            route: '/company',
          },
          {
            name: 'general.rituals',
            route: '/company/rituals',
          },
          {
            name: 'navHelper.evaluationsProgress',
            route: `/company/rituals/evaluations`,
          },
        ]
  } else if (from?.includes('teamSettings')) {
    const teamId = from.split('-')[1]
    return userRole && userRole === TeamRole.LEAD
      ? [
          {
            name: 'general.teamSettings',
            route: `/team/${teamId}/settings`,
          },
        ]
      : [
          {
            name: company?.name || '',
          },
          {
            name: 'general.teamSettings',
            route: `/team/${teamId}/settings`,
          },
        ]
  } else if (from?.includes('peopleProfile')) {
    const userId = from.split('-')[1]
    return [
      {
        name: 'general.profile',
        route: `/company/people/profile/${userId}/onboarding`,
      },
    ]
  } else if (from === 'rituals') {
    return [
      {
        name: 'general.company',
        route: '/company',
      },
      {
        name: 'general.rituals',
        route: '/company/rituals',
      },
    ]
  } else if (from === 'culture') {
    return [
      {
        name: company?.name || '',
      },
      {
        name: 'general.feedback.culture',
        route: paths.culture,
      },
    ]
  } else if (from === 'profiles') {
    return [
      {
        name: company?.name || '',
      },
      {
        name: 'general.profiles',
        route: '/company/profiles',
      },
    ]
  } else if (from === 'ownDashboard') {
    return [
      {
        name: 'general.dashboard',
        route: paths.personalProfile,
      },
    ]
  } else if (from === 'onboardingDashboard') {
    return [
      {
        name: 'navHelper.onboardingDashboard',
        route: `${paths.personalProfile}/onboarding`,
      },
    ]
  } else if (from === 'team') {
    return [
      {
        name: 'general.team',
        route: '/team',
      },
    ]
  } else if (from === 'companySettings') {
    return [
      {
        name: 'general.companySettings',
        route: '/company/settings',
      },
    ]
  } else if (from === 'peopleLibrary') {
    return [
      {
        name: 'general.companySettings',
        route: '/company/settings',
      },
      {
        name: 'peopleLibrary.title',
        route: '/company/settings/people',
      },
    ]
  } else if (from === 'teamLibrary') {
    return [
      {
        name: 'general.companySettings',
        route: '/company/settings',
      },
      {
        name: 'companySettings.teamLibrary',
        route: '/company/settings/teams',
      },
    ]
  } else {
    return [
      {
        name: company?.name || '',
      },
      {
        name: 'general.people',
        route: paths.people,
      },
    ]
  }
}
